import React, { useState, isValidElement } from 'react';
import { BsPatchCheckFill, BsPatchQuestionFill } from 'react-icons/bs';
import { FiCopy } from 'react-icons/fi';
import humanizeKey from 'humanize-key';

const toDisplayValue = (value: unknown): string | JSX.Element => {
  if (value === 'checked') return 'checked';
  if (value === '?') return '?';
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (isValidElement(value)) {
    return value;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }
  return String(value);
};

const getCopyableValue = (value: unknown): string => {
  const display = toDisplayValue(value);
  if (display === 'checked') return 'Yes';
  if (display === '?') return 'Unknown';
  if (typeof display === 'string') return display;
  return '';
};

const renderOptionalValue = (value: unknown) => {
  if (value === 'checked') {
    return <BsPatchCheckFill className="xa-attributes-list-value-true" />;
  }
  const display = toDisplayValue(value);
  return display === '?' ? (
    <BsPatchQuestionFill className="xa-attributes-list-value-unknown" />
  ) : (
    <>{display}</>
  );
};

const renderValue = (name: string | JSX.Element, value: unknown) => {
  switch (name) {
    default:
      return renderOptionalValue(value);
  }
};

interface Props {
  name: string | JSX.Element;
  value: unknown;
  onClick?: () => void;
  copyValue?: string;
}

const COPIED_RESET_MS = 2000;

const AttributeRow: React.FC<Props> = ({ name, value, onClick, copyValue }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = copyValue ?? getCopyableValue(value);
    if (text) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_MS);
    }
  };

  const handleValueContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const text = copyValue ?? getCopyableValue(value);
    if (text) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_MS);
    }
  };

  return (
    <div className="xa-attributes-list-row" onClick={onClick}>
      <div className="xa-attributes-list-label">{humanizeKey(name)}</div>
      <div className="xa-attributes-list-value-group">
        <div
          className="xa-attributes-list-value"
          onContextMenu={handleValueContextMenu}
          title="Right click to copy"
        >
          {renderValue(name, value)}
        </div>
        {copied && (
          <span className="xa-attributes-list-copied-notice">Copied!</span>
        )}
        <button
          type="button"
          className="xa-attributes-list-copy-btn"
          onClick={handleCopy}
          title="Copy"
          aria-label="Copy"
        >
          {copied ? (
            <BsPatchCheckFill className="xa-attributes-list-copy-btn-check" />
          ) : (
            <FiCopy />
          )}
        </button>
      </div>
    </div>
  );
};

export default AttributeRow;
